// Public video self-introduction - /video-intro/:token
// Track A / A7 (6 Sep 2026); rebuilt on branch assessment-ux-2026-09-06 for
//   (1) 60 / 120 / 180-second lengths - the invite carries one, the recorder hard-stops on it;
//   (2) the DCS look a candidate expects: cream ground, gold rule, the logo, the site's type,
//       the vendored ui primitives, phone-first touch targets.
// In-browser MediaRecorder, hard stop at max_seconds, preview, up to 3 takes (JM default),
// explicit consent checkbox BEFORE upload, upload with progress via XMLHttpRequest.
// Backend: routes/video_intro.py  GET /api/public/video-intro/{token}, POST .../upload (multipart)
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Toaster, toast } from 'sonner';
import {
  AlertTriangle, Camera, Check, CheckCircle2, Clock, Loader2, Mic, RefreshCw,
  RotateCcw, Send, Shield, ShieldCheck, Upload, Video,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/video-intro`;
const MAX_TAKES = 3;

// The one external asset on this page: the DCS mark used by the app sidebar.
const DCS_LOGO = 'https://customer-assets.emergentagent.com/job_talent-match-129/artifacts/10z830dt_Daruwallas%20Consultancy%20Services%20LLP%20Logo.png';
// Brand tokens, same values as index.css (--accent / .text-gold) and the DCS case-file template.
const GOLD = '#C9A54E';
const GOLD_LIGHT = '#E8C96A';
const GOLD_GRADIENT = `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`;
const RULE = 'bg-gradient-to-r from-[#C9A54E] via-[#E8C96A] to-[#C9A54E]';
const STEPS = ['Record', 'Review', 'Send'];

function pickMime() {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const list = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4'];
  for (const m of list) if (MediaRecorder.isTypeSupported(m)) return m;
  return undefined;
}

function mmss(total) {
  const s = Math.max(0, Math.round(total));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// 60 -> "60-second", 120 -> "2-minute", 180 -> "3-minute"
function lengthLabel(secs) {
  return secs >= 120 && secs % 60 === 0 ? `${secs / 60}-minute` : `${secs}-second`;
}

// --- brand chrome ------------------------------------------------------------------------------
function BrandBar({ kicker, right }) {
  return (
    <header className="bg-white border-b border-[#E5E1D5]" data-testid="dcs-brand-bar">
      <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={DCS_LOGO}
            alt="Daruwalla Consultancy Services"
            className="h-9 w-auto shrink-0"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="min-w-0 leading-tight">
            <div className="text-[13px] sm:text-sm font-semibold tracking-tight text-[#0F1729] truncate">
              Daruwalla Consultancy Services
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#C9A54E] truncate">
              {kicker}
            </div>
          </div>
        </div>
        {right}
      </div>
      <div className={`h-[3px] w-full ${RULE}`} />
    </header>
  );
}

function BrandFooter() {
  return (
    <footer className="mx-auto w-full max-w-2xl px-4 sm:px-6 pb-10 pt-8 text-center">
      <div className="mx-auto mb-4 h-px w-16 bg-[#E5E1D5]" />
      <p className="text-xs text-slate-500 leading-relaxed">
        Daruwalla Consultancy Services LLP &middot; Fort, Mumbai
        <br />
        Trouble with this page? Write to{' '}
        <a href="mailto:care@daruwallas.com" className="font-medium text-[#8A6D22] underline underline-offset-2">
          care@daruwallas.com
        </a>
      </p>
    </footer>
  );
}

function Shell({ kicker, right, children }) {
  return (
    <div className="min-h-screen bg-[#FDFCF9] text-[#0F1729] flex flex-col">
      <Toaster position="top-center" richColors />
      <BrandBar kicker={kicker} right={right} />
      <main className="flex-1 mx-auto w-full max-w-2xl px-4 sm:px-6 py-6 sm:py-8">{children}</main>
      <BrandFooter />
    </div>
  );
}

function Stepper({ active }) {
  return (
    <ol className="mb-5 flex items-center gap-2" aria-label="Progress" data-testid="video-stepper">
      {STEPS.map((label, i) => {
        const done = i < active;
        const now = i === active;
        return (
          <li key={label} className="flex flex-1 items-center gap-2 min-w-0">
            <span
              className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold transition-colors ${
                done ? 'text-white' : now ? 'text-white' : 'bg-[#EFE9DA] text-[#9C8A5F]'
              }`}
              style={done || now ? { background: GOLD_GRADIENT } : undefined}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={`truncate text-xs font-medium ${now ? 'text-[#0F1729]' : 'text-slate-500'}`}>{label}</span>
            {i < STEPS.length - 1 && <span className="hidden sm:block h-px flex-1 bg-[#E5E1D5]" />}
          </li>
        );
      })}
    </ol>
  );
}

// A cream callout with the gold left rule - the DCS case-file "verdict" block.
function Callout({ children, className = '' }) {
  return (
    <div className={`rounded-lg border border-[#E5E1D5] border-l-4 border-l-[#C9A54E] bg-[#FDFBF7] p-4 ${className}`}>
      {children}
    </div>
  );
}

function GoldButton({ children, className = '', style, ...props }) {
  return (
    <Button
      {...props}
      className={`h-12 w-full rounded-full text-[15px] font-semibold text-white shadow-sm transition-transform hover:brightness-[1.03] active:scale-[0.99] disabled:opacity-50 ${className}`}
      style={{ background: GOLD_GRADIENT, ...style }}
    >
      {children}
    </Button>
  );
}

function QuietButton({ children, className = '', ...props }) {
  return (
    <Button
      variant="outline"
      {...props}
      className={`h-12 rounded-full border-[#E5E1D5] bg-white text-[15px] font-semibold text-[#0F1729] hover:bg-[#FDFBF7] hover:text-[#0F1729] ${className}`}
    >
      {children}
    </Button>
  );
}

// The countdown ring: it always reads against the length THIS invite carries.
function CountdownRing({ elapsed, total, live, children }) {
  const R = 54;
  const C = 2 * Math.PI * R;
  const frac = total > 0 ? Math.min(1, elapsed / total) : 0;
  return (
    <div className="relative grid h-[124px] w-[124px] place-items-center">
      <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="60" cy="60" r={R} fill="none" stroke="#EFE9DA" strokeWidth="7" />
        <circle
          cx="60" cy="60" r={R} fill="none" stroke={live ? '#DC2626' : GOLD} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - frac)}
          style={{ transition: 'stroke-dashoffset 200ms linear, stroke 200ms linear' }}
        />
      </svg>
      {children}
    </div>
  );
}

function uploadWithProgress(url, form, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.upload.onprogress = (ev) => { if (ev.lengthComputable && onProgress) onProgress(Math.round((ev.loaded / ev.total) * 100)); };
    xhr.onload = () => {
      let body = null;
      try { body = JSON.parse(xhr.responseText); } catch { body = null; }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body);
      else { const err = new Error((body && body.detail) || `Upload failed (${xhr.status})`); err.status = xhr.status; reject(err); }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(form);
  });
}

export default function VideoIntroPage() {
  const { token } = useParams();
  const [stage, setStage] = useState('loading'); // loading | closed | intro | record | done
  const [info, setInfo] = useState(null);
  const [closed, setClosed] = useState({ title: '', message: '', kind: 'invalid' });
  const [permError, setPermError] = useState(null);
  const [streamReady, setStreamReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [durationSec, setDurationSec] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [take, setTake] = useState(0);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [slowUpload, setSlowUpload] = useState(false);
  const [micLevel, setMicLevel] = useState(0);

  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const tickRef = useRef(null);
  const hardStopRef = useRef(null);
  const slowRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  const maxSeconds = info?.max_seconds || 60;

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/${token}`)
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (cancelled) return;
        if (!res.ok) {
          setClosed(
            res.status === 503
              ? { kind: 'off', title: 'Not available just now', message: 'Video introductions are switched off at the moment. Please try this link again later, or reply to the message that brought you here.' }
              : res.status === 410
                ? { kind: 'expired', title: 'This link has closed', message: 'It has expired or was withdrawn. If you still want to send your introduction, ask your Daruwallas contact for a fresh link - it only takes a moment.' }
                : res.status === 429
                  ? { kind: 'busy', title: 'One moment', message: 'That was a lot of requests from your connection. Please wait a minute and reload this page.' }
                  : { kind: 'invalid', title: 'This link is not valid', message: 'Please open the exact link from your email or WhatsApp - copying part of it can break it.' },
          );
          setStage('closed');
          return;
        }
        setInfo(body);
        setStage(body.status === 'uploaded' ? 'done' : 'intro');
      })
      .catch(() => {
        if (cancelled) return;
        setClosed({ kind: 'network', title: 'Could not reach us', message: 'Check your internet connection and reload this page. Nothing you have done is lost.' });
        setStage('closed');
      });
    return () => { cancelled = true; };
  }, [token]);

  const stopStream = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setStreamReady(false);
    setMicLevel(0);
  }, []);

  useEffect(() => () => {
    stopStream();
    if (tickRef.current) clearInterval(tickRef.current);
    if (hardStopRef.current) clearTimeout(hardStopRef.current);
    if (slowRef.current) clearTimeout(slowRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [stopStream, previewUrl]);

  useEffect(() => {
    const v = liveRef.current;
    const s = streamRef.current;
    if (v && s && v.srcObject !== s) { v.srcObject = s; v.play().catch(() => {}); }
  }, [stage, streamReady, previewUrl]);

  const startCamera = useCallback(async () => {
    setPermError(null);
    setStage('record');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setPermError('This browser cannot record video on this page. Please open the link in Chrome or Safari on your phone.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }, audio: true });
      streamRef.current = stream;
      setStreamReady(true);
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (!audioCtxRef.current) return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) { const x = (buf[i] - 128) / 128; sum += x * x; }
          setMicLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3));
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch { /* meter is optional */ }
    } catch (err) {
      const name = (err && err.name) || '';
      const msg = name === 'NotAllowedError' ? 'Camera and microphone are blocked for this page. Tap the lock or camera icon next to the address bar, allow both, then tap Try again.'
        : name === 'NotFoundError' ? 'No camera or microphone was found on this device. Try opening this link on your phone.'
          : name === 'NotReadableError' ? 'Your camera is being used by another app. Close that app (video call, camera), then tap Try again.'
            : 'Could not start the camera. Tap Try again, or open this link on your phone.';
      setPermError(msg);
    }
  }, []);

  const stopRecorder = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (hardStopRef.current) { clearTimeout(hardStopRef.current); hardStopRef.current = null; }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') { try { rec.stop(); } catch { /* noop */ } }
  }, []);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) { toast.error('Camera not ready'); return; }
    if (typeof MediaRecorder === 'undefined') { toast.error('This browser cannot record video. Please use Chrome or Safari.'); return; }
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    setBlob(null);
    setDurationSec(0);
    const mime = pickMime();
    let rec;
    try { rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined); } catch (e) { toast.error(e.message); return; }
    chunksRef.current = [];
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
    rec.onstop = () => {
      const type = (chunksRef.current[0] && chunksRef.current[0].type) || mime || 'video/webm';
      const b = new Blob(chunksRef.current, { type });
      const secs = Math.min(maxSeconds, Math.max(1, (Date.now() - startTsRef.current) / 1000));
      setRecording(false);
      if (b.size === 0) { toast.error('No video was captured - please try again.'); return; }
      setBlob(b);
      setDurationSec(Math.round(secs * 10) / 10);
      setPreviewUrl(URL.createObjectURL(b));
    };
    startTsRef.current = Date.now();
    setElapsed(0);
    rec.start(1000);
    recorderRef.current = rec;
    setRecording(true);
    tickRef.current = setInterval(() => {
      const s = (Date.now() - startTsRef.current) / 1000;
      setElapsed(Math.min(maxSeconds, Math.floor(s)));
      if (s >= maxSeconds) stopRecorder();          // the hard stop, at THIS invite's length
    }, 200);
    // second belt on the same braces: timers are throttled in a backgrounded tab, this is not.
    hardStopRef.current = setTimeout(stopRecorder, maxSeconds * 1000 + 60);
  }, [maxSeconds, previewUrl, stopRecorder]);

  const retake = useCallback(() => {
    if (take + 1 >= MAX_TAKES) return;
    setTake((n) => n + 1);
    setBlob(null);
    setDurationSec(0);
    setElapsed(0);
    setConsent(false);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  }, [take, previewUrl]);

  const upload = useCallback(async () => {
    if (!blob || !consent || uploading) return;
    if (blob.size > (info?.max_bytes || 25 * 1024 * 1024)) { toast.error('The recording is too large - please record a shorter take.'); return; }
    setUploading(true);
    setProgress(0);
    setSlowUpload(false);
    slowRef.current = setTimeout(() => setSlowUpload(true), 15000);
    try {
      const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
      const form = new FormData();
      form.append('file', blob, `intro.${ext}`);
      form.append('consent_accepted', 'true');
      form.append('consent_text_version', info.consent.version);
      form.append('duration_seconds', String(durationSec));
      await uploadWithProgress(`${API}/${token}/upload`, form, setProgress);
      stopStream();
      setStage('done');
      toast.success('Your video has been uploaded');
    } catch (e) {
      if (e.status === 409) { stopStream(); setStage('done'); return; }
      toast.error(e.message);
    } finally {
      if (slowRef.current) { clearTimeout(slowRef.current); slowRef.current = null; }
      setSlowUpload(false);
      setUploading(false);
    }
  }, [blob, consent, uploading, info, durationSec, token, stopStream]);

  // ---- loading --------------------------------------------------------------------------------
  if (stage === 'loading') {
    return (
      <Shell kicker="Video introduction">
        <Card className="border-[#E5E1D5] shadow-sm" data-testid="video-loading">
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Loader2 className="h-7 w-7 animate-spin" style={{ color: GOLD }} />
            <p className="text-sm text-slate-500">Opening your invitation&hellip;</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // ---- closed / expired / off / invalid --------------------------------------------------------
  if (stage === 'closed') {
    const Icon = closed.kind === 'expired' ? Clock : closed.kind === 'network' ? RefreshCw : AlertTriangle;
    return (
      <Shell kicker="Video introduction">
        <Card className="border-[#E5E1D5] shadow-sm" data-testid="video-closed">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-[#F5EFDF]">
              <Icon className="h-7 w-7" style={{ color: GOLD }} />
            </div>
            <h1 className="text-xl font-semibold tracking-tight">{closed.title}</h1>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-600" data-testid="video-closed-message">
              {closed.message}
            </p>
            <QuietButton className="mt-6 w-full sm:w-auto sm:px-8" onClick={() => window.location.reload()}>
              <RefreshCw className="mr-2 h-4 w-4" />Reload
            </QuietButton>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // ---- done -----------------------------------------------------------------------------------
  if (stage === 'done') {
    return (
      <Shell kicker="Video introduction" right={<span className="hidden sm:inline text-xs font-medium text-slate-500">Complete</span>}>
        <Card className="border-[#E5E1D5] shadow-sm" data-testid="video-done">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full" style={{ background: 'rgba(201,165,78,0.14)' }}>
              <CheckCircle2 className="h-9 w-9" style={{ color: GOLD }} />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Video received</h1>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-600">
              Thank you{info?.candidate_first_name ? `, ${info.candidate_first_name}` : ''}. Your introduction is with the
              Daruwallas team. A consultant will review it along with your CV and come back to you about next steps.
            </p>
            <Callout className="mt-6 text-left">
              <p className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
                <Shield className="mt-0.5 h-4 w-4 shrink-0" style={{ color: GOLD }} />
                <span>
                  Your video is shared only with the employer considering you, and is deleted after 90 days. To have it
                  removed sooner, write to care@daruwallas.com.
                </span>
              </p>
            </Callout>
            <p className="mt-6 text-xs text-slate-400">You can close this page.</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // ---- intro ----------------------------------------------------------------------------------
  if (stage === 'intro') {
    return (
      <Shell
        kicker="Video introduction"
        right={<span className="rounded-full border border-[#E5E1D5] bg-[#FDFBF7] px-3 py-1 text-[11px] font-semibold text-[#8A6D22]">{mmss(maxSeconds)}</span>}
      >
        <Stepper active={0} />
        <Card className="border-[#E5E1D5] shadow-sm" data-testid="video-intro-card">
          <CardContent className="space-y-5 p-5 sm:p-7">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#C9A54E]">
                {lengthLabel(maxSeconds)} self-introduction
              </p>
              <h1 className="mt-1.5 text-2xl font-semibold leading-snug tracking-tight sm:text-3xl">
                Hi {info.candidate_first_name || 'there'} &mdash; tell us about yourself
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Daruwalla Consultancy Services would like a short video introduction to send alongside your CV. It takes
                about five minutes, and you record it right here.
              </p>
            </div>

            <Callout>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8A6D22]">What to say</p>
              <div className="whitespace-pre-line text-sm leading-relaxed text-[#0F1729]" data-testid="video-script">
                {info.script}
              </div>
            </Callout>

            <ul className="space-y-3 text-sm">
              {[
                { icon: Video, text: <>Recording stops automatically at <strong>{mmss(maxSeconds)}</strong> ({maxSeconds} seconds). You get up to {MAX_TAKES} takes and choose which one to send.</> },
                { icon: Camera, text: <>Find a <strong>well-lit, quiet</strong> spot, hold the phone steady and look into the camera.</> },
                { icon: ShieldCheck, text: <>Nothing is uploaded until you press <strong>Send</strong> &mdash; you preview it first and confirm your consent.</> },
                { icon: Clock, text: <>This link expires on {info.expires_at ? new Date(info.expires_at).toLocaleString() : 'the date in your invitation'}.</> },
              ].map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#F5EFDF]">
                    <Icon className="h-4 w-4" style={{ color: GOLD }} />
                  </span>
                  <span className="leading-relaxed text-slate-600">{text}</span>
                </li>
              ))}
            </ul>

            <GoldButton onClick={startCamera} data-testid="open-camera-btn">
              <Camera className="mr-2 h-5 w-5" />Open camera
            </GoldButton>
            <p className="text-center text-xs text-slate-400">Your browser will ask for camera and microphone access.</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // ---- record / review -------------------------------------------------------------------------
  const remaining = Math.max(0, maxSeconds - elapsed);
  const micPct = Math.round(micLevel * 100);
  const reviewing = !!blob && !recording;

  return (
    <Shell
      kicker="Video introduction"
      right={
        <span className="rounded-full border border-[#E5E1D5] bg-[#FDFBF7] px-3 py-1 text-[11px] font-semibold text-[#8A6D22]" data-testid="take-counter">
          Take {take + 1} of {MAX_TAKES}
        </span>
      }
    >
      <Stepper active={uploading ? 2 : reviewing ? 1 : 0} />
      <Card className="border-[#E5E1D5] shadow-sm" data-testid="video-record-card">
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div className="flex items-baseline justify-between gap-3">
            <h1 className="text-lg font-semibold tracking-tight sm:text-xl">
              {reviewing ? 'Check your take' : 'Record your introduction'}
            </h1>
            <span className="shrink-0 text-xs text-slate-500">up to {mmss(maxSeconds)}</span>
          </div>

          {!reviewing && (
            <div className="max-h-32 overflow-auto rounded-lg border border-[#E5E1D5] bg-[#FDFBF7] p-3 text-[13px] leading-relaxed whitespace-pre-line text-slate-700">
              {info.script}
            </div>
          )}

          <div className="relative overflow-hidden rounded-xl bg-black">
            {previewUrl ? (
              <video src={previewUrl} controls playsInline className="aspect-[3/4] w-full object-contain sm:aspect-video" data-testid="video-preview" />
            ) : (
              <video ref={liveRef} autoPlay muted playsInline className="aspect-[3/4] w-full scale-x-[-1] object-cover sm:aspect-video" data-testid="video-live" />
            )}
            {recording && (
              <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />REC
              </div>
            )}
            {(recording || (!previewUrl && streamReady)) && (
              <div className="pointer-events-none absolute right-3 top-3 rounded-full bg-black/60 px-3 py-1 font-mono text-[11px] font-semibold text-white backdrop-blur-sm" data-testid="video-countdown">
                {mmss(remaining)} left
              </div>
            )}
            {!previewUrl && !streamReady && !permError && (
              <div className="absolute inset-0 grid place-items-center bg-black/70 text-center text-sm text-white">
                <span className="flex flex-col items-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  Waiting for your camera&hellip;
                </span>
              </div>
            )}
          </div>

          {permError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="camera-error">
              <p className="flex items-start gap-2 text-sm font-medium text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {permError}
              </p>
              <QuietButton className="mt-3 w-full" onClick={() => { stopStream(); startCamera(); }} data-testid="retry-camera-btn">
                <RefreshCw className="mr-2 h-4 w-4" />Try again
              </QuietButton>
            </div>
          )}

          {!previewUrl && !permError && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Mic className="h-3.5 w-3.5" style={{ color: GOLD }} />
                Say &ldquo;hello&rdquo; &mdash; this bar should move.
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-[#EFE9DA]" aria-label="Microphone level">
                <div
                  className="h-full transition-[width] duration-75"
                  style={{ width: `${Math.max(2, micPct)}%`, background: micPct > 5 ? GOLD_GRADIENT : '#D8CFB6' }}
                />
              </div>
            </div>
          )}

          {/* the ring: elapsed against THIS invite's length */}
          {!reviewing && (
            <div className="flex flex-col items-center gap-3 pt-1">
              <CountdownRing elapsed={elapsed} total={maxSeconds} live={recording}>
                {recording ? (
                  <button
                    type="button"
                    onClick={stopRecorder}
                    aria-label="Stop recording"
                    data-testid="stop-recording-btn"
                    className="grid h-[84px] w-[84px] place-items-center rounded-full bg-red-600 text-white shadow-md transition active:scale-95"
                  >
                    <span className="h-7 w-7 rounded-[6px] bg-white" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecording}
                    disabled={!streamReady || !!permError}
                    aria-label="Start recording"
                    data-testid="start-recording-btn"
                    className="grid h-[84px] w-[84px] place-items-center rounded-full text-white shadow-md transition active:scale-95 disabled:opacity-40"
                    style={{ background: GOLD_GRADIENT }}
                  >
                    <span className="h-8 w-8 rounded-full bg-white/95" />
                  </button>
                )}
              </CountdownRing>
              <p className="font-mono text-lg font-semibold tabular-nums" data-testid="video-timer">
                {mmss(elapsed)} <span className="text-sm font-normal text-slate-400">/ {mmss(maxSeconds)}</span>
              </p>
              <p className="text-center text-xs text-slate-500">
                {recording ? 'Recording. Tap the square to stop early.' : streamReady ? 'Tap the circle when you are ready.' : 'Waiting for the camera…'}
              </p>
            </div>
          )}

          {reviewing && (
            <>
              <div className="flex items-center justify-between rounded-lg border border-[#E5E1D5] bg-[#FDFBF7] px-4 py-3 text-sm">
                <span className="font-medium text-[#0F1729]">Your take</span>
                <span className="font-mono text-xs text-slate-500" data-testid="video-duration">
                  {mmss(durationSec)} of {mmss(maxSeconds)}
                </span>
              </div>

              <Callout>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(!!v)}
                    disabled={uploading}
                    className="mt-0.5 h-5 w-5 shrink-0 border-[#C9A54E] data-[state=checked]:border-[#C9A54E] data-[state=checked]:bg-[#C9A54E]"
                    data-testid="consent-checkbox"
                  />
                  <Label htmlFor="consent" className="cursor-pointer text-xs leading-relaxed text-slate-700">
                    <span className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8A6D22]">
                      <ShieldCheck className="h-3.5 w-3.5" />Consent ({info.consent.version})
                    </span>
                    {info.consent.text}
                  </Label>
                </div>
              </Callout>

              {uploading && (
                <div className="space-y-2 rounded-lg border border-[#E5E1D5] bg-white p-4" data-testid="upload-progress">
                  <div className="flex items-center justify-between text-xs font-medium text-slate-600">
                    <span className="flex items-center gap-2"><Upload className="h-3.5 w-3.5" style={{ color: GOLD }} />Sending your video</span>
                    <span className="font-mono tabular-nums">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2 bg-[#EFE9DA] [&>*]:bg-[#C9A54E]" />
                  <p className="text-[11px] leading-relaxed text-slate-500">
                    {slowUpload
                      ? 'Still going. A weak signal can make this slow - please keep this page open, it will finish.'
                      : 'Please keep this page open until it finishes.'}
                  </p>
                </div>
              )}

              <div className="space-y-2 pt-1">
                <GoldButton onClick={upload} disabled={!consent || uploading} data-testid="send-video-btn">
                  {uploading
                    ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Sending&hellip;</>
                    : <><Send className="mr-2 h-5 w-5" />Send this take</>}
                </GoldButton>
                {take + 1 < MAX_TAKES && (
                  <QuietButton className="w-full" onClick={retake} disabled={uploading} data-testid="retake-btn">
                    <RotateCcw className="mr-2 h-4 w-4" />Record again ({MAX_TAKES - take - 1} left)
                  </QuietButton>
                )}
                {!consent && !uploading && (
                  <p className="text-center text-xs text-slate-500">Tick the consent box above to enable Send.</p>
                )}
                {take + 1 >= MAX_TAKES && !uploading && (
                  <p className="text-center text-xs text-slate-400">This is your last take.</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </Shell>
  );
}
